import { useEffect, useRef } from "react";
import type { JSX } from "react";

import type { DataTableFilterType } from "./DataTable.types";
import { createDataTableColumnHelper } from "./dataTableFeatures";
import type { DataTableColumnDef } from "./dataTableFeatures";

export type RWCDataTableRow = Record<string, unknown>;

export interface RWCDataTableColumn {
  key: string;
  label: string;
  sortable?: boolean;
  width?: string;
  minWidth?: string;
  maxWidth?: string;
  truncate?: boolean;
  filter?: DataTableFilterType;
  render?: (row: RWCDataTableRow) => Node | string | number | null | undefined;
}

interface RWCDataTableNodeCellProps {
  node: Node;
}

function RWCDataTableNodeCell({
  node,
}: RWCDataTableNodeCellProps): JSX.Element {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = ref.current;

    if (!container) {
      return;
    }

    container.appendChild(node);

    return () => {
      if (node.parentNode === container) {
        container.removeChild(node);
      }
    };
  }, [node]);

  return <div ref={ref} />;
}

const helper = createDataTableColumnHelper<RWCDataTableRow>();

export function buildRWCColumns(
  columns: RWCDataTableColumn[],
): DataTableColumnDef<RWCDataTableRow>[] {
  return helper.columns(
    columns.map((column) => {
      const render = column.render;

      return helper.accessor((row) => row[column.key], {
        id: column.key,
        header: column.label,
        enableSorting: column.sortable ?? true,
        meta: {
          width: column.width,
          minWidth: column.minWidth,
          maxWidth: column.maxWidth,
          truncate: column.truncate,
          filter: column.filter,
        },
        ...(render
          ? {
              cell: (ctx: { row: { original: RWCDataTableRow } }) => {
                const result = render(ctx.row.original);

                return result instanceof Node ? (
                  <RWCDataTableNodeCell node={result} />
                ) : (
                  result
                );
              },
            }
          : {}),
      });
    }),
  );
}
